import { c as _c } from "react/compiler-runtime";
import Tooltip from '@precis-digital/kurama/Tooltip';
import { useTheme } from '@precis-digital/kurama/utils';
import { isValidElement, type ReactNode, type ReactElement } from 'react';
import type { ElementDescriptionDimension, ElementDescriptionMetric } from '../../../internalTypes';
import type { ChartCardTFunction, ChartDataRow, ChartDataValue } from '../../../types';
import { elementDescriptionIsDimension } from '../../../utils/elementDescription';
import { defaultTFunction, formatMetricValue, isNullDimensionValue } from '../../../utils/generics';
import { NULL_DIMENSION_VALUE_DISPLAY } from '../../../constants';
import { useChartCardDefaults } from '../../../hooks/useChartCardDefaults';
import { StyledBodyText, StyledCellContainer } from '../styled';
import { getBodyCellFormattedValue, getColumnDataKey } from '../utils';
import { ROW_SELECTION_COLUMN_ID } from '../constants';
import TableBodyCellRowSelection from './TableBodyCellRowSelection';
import type { ChartCardTableBodyCellContextResolved } from '../tanstack';
import type { CellAlignment } from '../types';
const NullDimensionValueCell = t0 => {
  const $ = _c(3);
  const {
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <StyledBodyText variant="body2" textAlign="left">{NULL_DIMENSION_VALUE_DISPLAY}</StyledBodyText>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <Tooltip kind="singleline" title={title}>{t1}</Tooltip>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const DefaultTableBodyCellContent = t0 => {
  const $ = _c(26);
  const {
    dataKey,
    value,
    elementDescription,
    alignProp,
    enableNullDimensionValue,
    t
  } = t0;
  const defaults = useChartCardDefaults();
  const theme = useTheme();
  if (enableNullDimensionValue && isNullDimensionValue(value) && elementDescriptionIsDimension(elementDescription)) {
    let t1;
    if ($[0] !== t) {
      t1 = t("noData");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <NullDimensionValueCell title={t1} />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (elementDescription == null) {
    if (value == null) {
      const t1 = value ?? "";
      let t2;
      if ($[4] !== t1) {
        t2 = <StyledBodyText variant="body2" textAlign="left">{t1}</StyledBodyText>;
        $[4] = t1;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      return t2;
    }
    let t1;
    if ($[6] !== dataKey || $[7] !== defaults || $[8] !== value) {
      t1 = typeof value === "number" ? formatMetricValue(dataKey, {
        type: "number"
      }, value, defaults.table.format) : String(value);
      $[6] = dataKey;
      $[7] = defaults;
      $[8] = value;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    const formattedValue = t1;
    const t2 = typeof value === "number" ? "right" : "left";
    let t3;
    if ($[10] !== formattedValue || $[11] !== t2) {
      t3 = <StyledBodyText variant="body2" textAlign={t2}>{formattedValue}</StyledBodyText>;
      $[10] = formattedValue;
      $[11] = t2;
      $[12] = t3;
    } else {
      t3 = $[12];
    }
    return t3;
  }
  let align;
  let cellValue;
  let t1;
  let t2;
  if ($[13] !== alignProp || $[14] !== dataKey || $[15] !== defaults || $[16] !== elementDescription || $[17] !== theme || $[18] !== value) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      ({
        value: cellValue,
        align
      } = getBodyCellFormattedValue({
        defaults,
        dataKey,
        elementDescription,
        alignProp,
        value,
        textVariant: "body2",
        theme
      }) ?? {});
      if (typeof cellValue === "string") {
        t2 = <StyledBodyText variant="body2" textAlign={align}>{cellValue}</StyledBodyText>;
        break bb0;
      }
      t1 = isValidElement(cellValue);
    }
    $[13] = alignProp;
    $[14] = dataKey;
    $[15] = defaults;
    $[16] = elementDescription;
    $[17] = theme;
    $[18] = value;
    $[19] = align;
    $[20] = cellValue;
    $[21] = t1;
    $[22] = t2;
  } else {
    align = $[19];
    cellValue = $[20];
    t1 = $[21];
    t2 = $[22];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  if (t1) {
    return cellValue;
  }
  const t3 = value ?? "";
  let t4;
  if ($[23] !== align || $[24] !== t3) {
    t4 = <StyledBodyText variant="body2" textAlign={align}>{t3}</StyledBodyText>;
    $[23] = align;
    $[24] = t3;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  return t4;
};
const TableBodyCellContent = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: ChartCardTableBodyCellContextResolved<DataKey, DataRow>): ReactElement => {
  const {
    meta: contextMeta,
    ...context
  } = props;
  const {
    column,
    getValue,
    row
  } = context;
  const dataKey = getColumnDataKey<DataKey, DataRow>(column);
  const {
    elementsDescriptionsMap,
    rowSelected,
    enableNullDimensionValue,
    t = defaultTFunction
  } = contextMeta.chartCard;
  const elementDescription = dataKey == null ? undefined : elementsDescriptionsMap?.[dataKey] as ElementDescriptionMetric | ElementDescriptionDimension | undefined;
  const {
    cell: columnCell,
    meta: columnMeta
  } = column.columnDef;
  const {
    align: alignProp
  } = columnMeta?.chartCard ?? {};
  let cellContent: ReactNode;
  if (column.id === ROW_SELECTION_COLUMN_ID) {
    cellContent = <TableBodyCellRowSelection<DataKey, DataRow> row={row} rowSelected={rowSelected} />;
  } else if (typeof columnCell === 'function') {
    cellContent = columnCell(props);
  } else {
    cellContent = columnCell;
  }
  if (isValidElement(cellContent)) {
    return <StyledCellContainer width="100%" justifyContent="start">
        {cellContent}
      </StyledCellContainer>;
  }
  let value = getValue();
  if (dataKey == null) {
    return <StyledBodyText variant="body2" textAlign="left">
        {value ?? ''}
      </StyledBodyText>;
  }
  const rowValue: NoInfer<DataRow[DataKey]> = row.original[dataKey];
  value ??= rowValue ?? null;
  return <DefaultTableBodyCellContent dataKey={dataKey} value={value} elementDescription={elementDescription} alignProp={alignProp} enableNullDimensionValue={enableNullDimensionValue} t={t} />;
};
export default TableBodyCellContent;